import Box from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import {
  humanizeOwnerName,
  StarCheckbox,
  TagsCollection,
} from '@mb.io/core-components';

const MAX_TEXT_LENGTH = 90;

const useStyles = makeStyles(theme => ({
  typography: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.70)'
        : 'rgba(0, 0, 0, 0.60)',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  cardHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  cardHeader: {
    margin: 0,
    padding: 0,
    maxWidth: '80%',
    wordBreak: 'break-word',
  },
  cardOwner: {
    marginTop: '0.5rem',
  },
  cardKindType: {
    marginTop: '0.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '0.5rem',
  },
  kindLabel: {
    marginRight: '2.5rem',
  },
  cardDescription: {
    marginTop: '1rem',
    paddingBottom: '0.5rem',
    overflow: 'hidden',
    flexGrow: 1,
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}));

export type CardContentProps = {
  item: any;
  index: number;
};

export const EntityCardContent = ({ item, index }: CardContentProps) => {
  const classes = useStyles();

  const capitalize = (inputString: string) => {
    if (!inputString) {
      return ''; // Or handle null/undefined input as needed
    }
    return inputString.charAt(0).toUpperCase() + inputString.slice(1);
  };

  const extractSentencesUpToMaxLength = (text: string): string => {
    if (text.length <= MAX_TEXT_LENGTH) {
      return text;
    }

    const endIndex = text.lastIndexOf('.', MAX_TEXT_LENGTH);

    if (endIndex === -1) {
      return `${text.substring(0, MAX_TEXT_LENGTH)}...`;
    }

    return text.substring(0, endIndex + 1);
  };

  return (
    <Box className={classes.cardContent} key={`${item.name}-${index}`}>
      <Box className={classes.cardHeaderContainer}>
        <CardHeader
          title={capitalize(item?.title ?? item?.name)}
          className={classes.cardHeader}
          data-testid="card-title"
        />
        <StarCheckbox
          kind={item.kind}
          name={item.name}
          namespace={item.namespace}
        />
      </Box>

      <Box data-testid="card-kind-and-type" className={classes.cardKindType}>
        {item.kind && (
          <Typography
            variant="body2"
            className={`${classes.typography} ${classes.kindLabel} `}
          >
            <strong>Kind:</strong> {item.kind}
          </Typography>
        )}
        {item.type && (
          <Typography variant="body2" className={classes.typography}>
            <strong>Type:</strong> {item.type}
          </Typography>
        )}
      </Box>

      {item?.owner && (
        <Box data-testid="card-owner" className={classes.cardOwner}>
          <Typography variant="body2" className={classes.typography}>
            <strong>Owner:</strong> {humanizeOwnerName(item?.owner)}
          </Typography>
        </Box>
      )}

      <Typography
        className={`${classes.typography} ${classes.cardDescription}`}
        variant="body1"
        data-testid="card-description"
      >
        {extractSentencesUpToMaxLength(item.description ?? '')}
      </Typography>

      <Box className={classes.tagsContainer}>
        <TagsCollection tags={item.tags} maxVisibleTags={2} chipSize="small" />
      </Box>
    </Box>
  );
};
